import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowRight,
  Sparkles,
  Users,
  BookOpen,
  BarChart3,
  MessageSquare,
  Wand2,
  ChevronDown,
  Activity,
  Eye,
  Bell,
  Zap,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

export function Landing() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[hsl(220,40%,98%)] via-background to-secondary/40">
      <AmbientParticles />
      <Header />
      <Hero />
      <LiveStrip />
      <FeatureGrid />
      <CTA />
      <Footer />
    </div>
  );
}

/* ─── Top nav ───────────────────────────────────────────────────────────── */

function Header() {
  return (
    <header className="container relative z-10 flex items-center justify-between py-6">
      <Link to="/" className="flex items-center gap-2.5">
        <img
          src="/brand/eduborg-mascot.png"
          alt=""
          aria-hidden
          width={40}
          height={40}
          className="animate-mascot-idle"
        />
        <span className="font-display text-2xl font-bold tracking-tight">
          <span className="text-primary">Edu</span>
          <span className="text-accent">Borg</span>
        </span>
      </Link>
      <nav className="hidden items-center gap-7 text-sm font-medium text-muted-foreground md:flex">
        <a href="#about" className="transition-colors hover:text-foreground">
          About
        </a>
        <a href="#pricing" className="transition-colors hover:text-foreground">
          Pricing
        </a>
        <a href="#contact" className="transition-colors hover:text-foreground">
          Contact
        </a>
        <Link to="/auth/sign-in" className="transition-colors hover:text-foreground">
          Sign In
        </Link>
      </nav>
      <Link
        to="/auth/sign-up"
        className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:bg-primary/90 hover:shadow-md"
      >
        Get Started
      </Link>
    </header>
  );
}

/* ─── Hero ──────────────────────────────────────────────────────────────── */

function Hero() {
  return (
    <section className="container relative z-10 grid items-center gap-10 py-12 md:grid-cols-2 md:gap-6 md:py-20">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="space-y-7"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.05, duration: 0.4 }}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-card/80 px-3.5 py-1.5 text-xs font-medium text-muted-foreground shadow-sm backdrop-blur"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
          </span>
          Your AI Teaching Partner is Always On
        </motion.div>

        <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-5xl md:text-6xl">
          Meet <span className="text-accent">EduBorg</span>, the first classroom that is{' '}
          <span className="relative inline-block">
            actually alive
            <motion.span
              aria-hidden
              className="absolute inset-x-0 -bottom-1 h-1 origin-left rounded-full bg-gradient-to-r from-accent/0 via-accent to-accent/0"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.6, duration: 0.7 }}
            />
          </span>
          .
        </h1>

        <p className="max-w-xl text-lg text-muted-foreground">
          EduBorg isn&apos;t a tool you open &mdash; it&apos;s a{' '}
          <span className="font-semibold text-foreground">presence</span> that watches, learns, and
          acts alongside you.
        </p>

        <p className="max-w-xl text-base text-muted-foreground">
          It builds your courses. Monitors your students. Flags risks before they become failures.
          And celebrates every milestone along the way.
        </p>

        <p className="font-display text-lg font-semibold text-accent">
          Your classroom has never been this intelligent.
        </p>

        <div className="flex flex-wrap items-center gap-3 pt-2">
          <Link
            to="/auth/sign-up?role=teacher"
            className="group inline-flex items-center gap-1.5 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-md transition-all hover:translate-y-[-1px] hover:shadow-lg"
          >
            Start Teaching
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
          <Link
            to="/auth/sign-up?role=student"
            className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground shadow-sm transition-all hover:bg-secondary hover:shadow-md"
          >
            Join as Student
          </Link>
          <a
            href="#how"
            className="group inline-flex items-center gap-1.5 px-3 py-3 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            See How It Works
            <ChevronDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
          </a>
        </div>
      </motion.div>

      <HeroVisual />
    </section>
  );
}

function HeroVisual() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8, delay: 0.1 }}
      className="relative mx-auto w-full max-w-xl"
    >
      {/* Ambient glow behind hero */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 translate-x-6 translate-y-6 rounded-full bg-gradient-to-br from-accent/25 via-transparent to-transparent blur-3xl"
      />
      <div
        aria-hidden
        className="absolute inset-0 -z-10 -translate-x-6 -translate-y-6 rounded-full bg-gradient-to-tl from-primary/15 via-transparent to-transparent blur-3xl"
      />

      <motion.img
        src="/brand/hero-tree.png"
        alt="An open book with a tree of ideas growing out of it"
        className="relative w-full"
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Floating accent dots near the tree */}
      <FloatingDots />
    </motion.div>
  );
}

function FloatingDots() {
  const reduce = useReducedMotion();
  if (reduce) return null;

  const dots = [
    { x: '10%', y: '12%', size: 8, color: 'bg-accent', delay: 0 },
    { x: '78%', y: '8%', size: 10, color: 'bg-gold', delay: 0.4 },
    { x: '92%', y: '38%', size: 6, color: 'bg-accent', delay: 0.8 },
    { x: '4%', y: '52%', size: 7, color: 'bg-primary', delay: 1.2 },
    { x: '85%', y: '70%', size: 9, color: 'bg-gold', delay: 1.6 },
    { x: '20%', y: '82%', size: 6, color: 'bg-accent', delay: 2.0 },
  ];

  return (
    <>
      {dots.map((d, i) => (
        <motion.span
          key={i}
          aria-hidden
          className={cn('absolute rounded-full opacity-70', d.color)}
          style={{ left: d.x, top: d.y, width: d.size, height: d.size }}
          animate={{ y: [0, -10, 0], opacity: [0.5, 0.9, 0.5] }}
          transition={{
            duration: 3 + i * 0.3,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: d.delay,
          }}
        />
      ))}
    </>
  );
}

/* ─── Ambient page-level particles ─────────────────────────────────────── */

function AmbientParticles() {
  const reduce = useReducedMotion();
  if (reduce) return null;

  const particles = Array.from({ length: 14 }).map((_, i) => ({
    left: `${(i * 73) % 100}%`,
    top: `${(i * 41) % 100}%`,
    size: 4 + ((i * 7) % 6),
    duration: 8 + ((i * 3) % 8),
    delay: (i * 0.7) % 6,
    color: i % 3 === 0 ? 'bg-accent/40' : i % 3 === 1 ? 'bg-gold/40' : 'bg-primary/30',
  }));

  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {particles.map((p, i) => (
        <motion.span
          key={i}
          className={cn('absolute rounded-full blur-[1px]', p.color)}
          style={{ left: p.left, top: p.top, width: p.size, height: p.size }}
          animate={{ y: [0, -40, 0], x: [0, 10, 0], opacity: [0.3, 0.6, 0.3] }}
          transition={{
            duration: p.duration,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: p.delay,
          }}
        />
      ))}
    </div>
  );
}

/* ─── Live Strip ─ "EduBorg is doing things right now" ─────────────────── */

function LiveStrip() {
  const events = [
    { icon: Eye, text: 'Watching 24 student sessions across your classes' },
    { icon: Bell, text: 'Flagged 3 students at risk in Algebra II' },
    { icon: Wand2, text: 'Drafted lesson plan for Photosynthesis (5-min)' },
    { icon: Zap, text: 'Generated reinforcement quiz for Chapter 7' },
    { icon: Activity, text: 'Discussion sentiment in Biology 101: rising' },
    { icon: MessageSquare, text: 'Drafted reply to 6 student messages' },
  ];
  // duplicate so the marquee loops seamlessly
  const loop = [...events, ...events];

  return (
    <section className="container relative z-10 py-6">
      <div className="overflow-hidden rounded-2xl border border-border bg-card/60 px-4 py-3 backdrop-blur">
        <div className="flex items-center gap-3">
          <div className="flex shrink-0 items-center gap-2 pr-3">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-60" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-accent" />
            </span>
            <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
              Live
            </span>
          </div>
          <div className="relative flex-1 overflow-hidden">
            <motion.div
              className="flex gap-10 whitespace-nowrap"
              animate={{ x: ['0%', '-50%'] }}
              transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
            >
              {loop.map((e, i) => (
                <span key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <e.icon className="h-4 w-4 text-accent" aria-hidden />
                  {e.text}
                </span>
              ))}
            </motion.div>
            <div className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-card to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-card to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Feature grid ─────────────────────────────────────────────────────── */

function FeatureGrid() {
  const features = [
    {
      icon: Wand2,
      title: 'AI-built lessons, aligned to objectives',
      body: 'Type a topic and a grade level. EduBorg drafts the lesson, the activities, and a quiz — all aligned to your objectives.',
      stat: '60s',
      statLabel: 'avg. course-skeleton time',
    },
    {
      icon: MessageSquare,
      title: 'Adaptive Study Room',
      body: 'Students study one-on-one with EduBorg. It hints before answers and remembers what to reinforce.',
      stat: '1:1',
      statLabel: 'tutoring at scale',
    },
    {
      icon: BarChart3,
      title: 'Live insights, not lifeless reports',
      body: 'Risk radar, engagement signals, and weekly pulse digests — animated, contextual, and surfaced when they matter.',
      stat: 'Live',
      statLabel: 'updates, every minute',
    },
    {
      icon: BookOpen,
      title: 'A growing library',
      body: 'Every lesson, quiz, and rubric becomes searchable for the whole community. Borrow, remix, and contribute.',
      stat: '∞',
      statLabel: 'shared by educators',
    },
    {
      icon: Users,
      title: 'Schools and solo teachers',
      body: 'Run a district. Or claim your own branded space and share a link with your learners. Same engine, different scale.',
      stat: '1–100k',
      statLabel: 'students per tenant',
    },
    {
      icon: Sparkles,
      title: 'EduBorg, always there',
      body: 'A small companion in the corner that watches the page and proactively whispers what you might want next.',
      stat: '24/7',
      statLabel: 'present, never noisy',
    },
  ];

  return (
    <section id="how" className="container relative z-10 py-20">
      <div className="mx-auto max-w-2xl text-center">
        <motion.h2
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          className="font-display text-3xl font-bold tracking-tight sm:text-4xl"
        >
          Everything an LCMS does — and the things they forgot.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="mt-4 text-muted-foreground"
        >
          Discussions, gradebook, assignments, calendar, announcements, messaging, files, roles —
          plus the AI layer most platforms only paint on top.
        </motion.p>
      </div>

      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {features.map((f, i) => (
          <motion.div
            key={f.title}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.4, delay: i * 0.06 }}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
            className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-sm transition-shadow hover:shadow-xl"
          >
            {/* Hover glow */}
            <span
              aria-hidden
              className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-accent/10 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100"
            />
            <div className="relative flex items-start justify-between">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent">
                <f.icon className="h-5 w-5" aria-hidden />
              </div>
              <div className="text-right">
                <div className="font-display text-2xl font-bold text-foreground">{f.stat}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {f.statLabel}
                </div>
              </div>
            </div>
            <h3 className="relative mt-5 font-display text-lg font-semibold">{f.title}</h3>
            <p className="relative mt-2 text-sm text-muted-foreground">{f.body}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ─── CTA ───────────────────────────────────────────────────────────────── */

function CTA() {
  return (
    <section id="pricing" className="container relative z-10 py-20">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary via-primary to-[hsl(220,80%,18%)] px-8 py-16 text-primary-foreground shadow-2xl sm:px-16"
      >
        {/* Decorative orbs */}
        <span
          aria-hidden
          className="absolute -left-20 -top-20 h-64 w-64 rounded-full bg-accent/30 blur-3xl"
        />
        <span
          aria-hidden
          className="absolute -bottom-24 right-1/4 h-72 w-72 rounded-full bg-gold/20 blur-3xl"
        />

        <motion.img
          src="/brand/eduborg-mascot.png"
          alt=""
          aria-hidden
          className="absolute -bottom-6 -right-6 w-44 opacity-95 sm:w-60"
          animate={{ y: [0, -6, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
        />

        <div className="relative z-10 max-w-xl space-y-5">
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
            Ready to teach with EduBorg?
          </h2>
          <p className="text-primary-foreground/80">
            Free for individual teachers to start. Schools and institutions get a tailored plan.
            Your branded space goes live in minutes.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/auth/sign-up"
              className="inline-flex items-center gap-1.5 rounded-xl bg-accent px-5 py-3 text-sm font-semibold text-accent-foreground shadow transition-shadow hover:shadow-lg"
            >
              Create your space <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="mailto:hello@eduborg.com"
              className="inline-flex items-center gap-1.5 rounded-xl border border-primary-foreground/20 px-5 py-3 text-sm font-semibold transition-colors hover:bg-primary-foreground/10"
            >
              Talk to us
            </a>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

/* ─── Footer ───────────────────────────────────────────────────────────── */

function Footer() {
  return (
    <footer className="relative z-10 border-t border-border">
      <div className="container flex flex-col items-center justify-between gap-3 py-8 text-sm text-muted-foreground sm:flex-row">
        <div className="flex items-center gap-2">
          <img src="/brand/eduborg-mascot.png" alt="" width={20} height={20} aria-hidden />
          <span>© {new Date().getFullYear()} EduBorg — a TSYBORG venture.</span>
        </div>
        <div className="flex gap-5">
          <Link to="/privacy" className="hover:text-foreground">
            Privacy
          </Link>
          <a href="/terms" className="hover:text-foreground">
            Terms
          </a>
          <a href="/contact" className="hover:text-foreground">
            Contact
          </a>
        </div>
      </div>
    </footer>
  );
}
